import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Box, HStack, Image, Input, Text, VStack } from 'native-base';
import { PaymentInputsWrapper, usePaymentInputs } from 'react-payment-inputs/es';
import { css } from 'styled-components';
import images from 'react-payment-inputs/images';
import { Icon } from '../../Common/components/Icon';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { PaymentComponentType } from '../types/types';
import { usePowerboard } from '../hooks/usePowerboard';

export const PowerBoardComponent = forwardRef((props: PaymentComponentType, ref) => {
  const [cardNumber, setCardNumber] = useState('');
  const [cardExp, setCardExp] = useState('');
  const [cardCCV, setCardCCV] = useState('');
  const [cardHolder, setCardHolder] = useState('');

  const { createToken } = usePowerboard("leafdoctors");

  const {
    wrapperProps,
    getCardImageProps,
    getCardNumberProps,
    getExpiryDateProps,
    getCVCProps,
    meta,
  } = usePaymentInputs();

  const [isCardValid, setIsCardValid] = useState(false);

  useEffect(() => {
    const isValid = meta?.error === undefined;
    setIsCardValid(isValid);
    props.setCardCompleted(isValid);
  }, [meta?.error]);

  useImperativeHandle(ref, () => ({
    confirmPayment: () => {
      // console.log('PowerBoardComponent - confirmPayment', cardHolder);
      const promise = new Promise(async (accept, reject) => {
        try {
          const cardExpSplit = cardExp.split('/');
          const cardExpMonth = cardExpSplit[0].trim();
          const cardExpYear = cardExpSplit[1].trim();

          const token = await createToken(
            cardNumber,
            cardHolder,
            cardCCV,
            cardExpMonth,
            cardExpYear,
          );

          if (!token) {
            reject();
            return;
          }

          const payload = JSON.stringify({
            type: 'newcard',
            token,
            cardHolder,
            gateway: 'powerboard',
          });

          accept(payload);
        } catch (e) {
          reject(e);
        }
      });

      return promise;
    },
  }));

  return (
    <VStack space={1} opacity={props.disabled ? 0.5 : 1}>
      <HStack space={2} alignItems={'center'}>
        <Text color={'paymentBoxTitle'} fontWeight={'600'} flex={1}>
          Your Payment Card
        </Text>
        <Image
          borderRadius={'sm'}
          h={8}
          w={10}
          source={require('../../Assets/PaymentMethods/1.png')}
        />
        <Image
          borderRadius={'sm'}
          h={8}
          w={10}
          source={require('../../Assets/PaymentMethods/2.png')}
        />
        <Image
          borderRadius={'sm'}
          h={8}
          w={10}
          source={require('../../Assets/PaymentMethods/22.png')}
        />
      </HStack>

      <Input
        placeholder={'Card Holder Name'}
        value={cardHolder}
        h={12}
        bg={'white'}
        size={'xl'}
        onChangeText={(text) => setCardHolder(text)}
      />

      <PaymentInputsWrapper
        {...wrapperProps}
        styles={{
          errorText: {
            base: css`
              color: red;
              font-size: 14px;
            `,
          },
        }}
      >
        <svg {...getCardImageProps({ images })} />

        <input
          value={cardNumber}
          disabled={props.disabled}
          {...getCardNumberProps({
            onChange: (e) => {
              setCardNumber(e.target.value);
            },
          })}
        />

        <input
          value={cardExp}
          disabled={props.disabled}
          {...getExpiryDateProps({
            onChange: (e) => {
              setCardExp(e.target.value);
            },
          })}
        />

        <input
          value={cardCCV}
          disabled={props.disabled}
          {...getCVCProps({
            onChange: (e) => {
              setCardCCV(e.target.value);
            },
          })}
        />

        {isCardValid && (
          <Box position={'absolute'} right={0}>
            <Icon color={'green'} icon={faCircleCheck} />
          </Box>
        )}
      </PaymentInputsWrapper>
    </VStack>
  );
});
